'use client';

import { useEffect, useState } from 'react';

interface UseRegisterServiceWorkerOptions {
  /**
   * When `false`, the hook skips registration entirely *and* unregisters
   * any previously-installed SW for this scope. That cleanup matters for
   * users who registered the SW on a stage where the feature flag was
   * later turned off — without it they'd keep the stale SW (and the push
   * subscription it owns) forever.
   *
   * Defaults to `true` for backwards compatibility.
   */
  enabled?: boolean;
}

/**
 * Register the static `/sw.js` service worker on mount.
 *
 * - SSR-safe: the effect only runs in the browser.
 * - Skipped when the browser doesn't expose `serviceWorker` (older Safari,
 *   Firefox in private mode, etc.).
 * - Logs but does not throw on registration failure: a missing SW degrades
 *   gracefully to "no push, no app badge in background" without breaking
 *   the rest of the app.
 *
 * Mount once near the top of the tree (e.g. in `MainLayout`).
 */
export function useRegisterServiceWorker({ enabled = true }: UseRegisterServiceWorkerOptions = {}): {
  registration: ServiceWorkerRegistration | null;
  isSupported: boolean;
} {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;

    setIsSupported(true);

    let cancelled = false;

    if (!enabled) {
      // Tear down any leftover SW from a previous (enabled) build so the
      // browser stops dispatching push events / showing notifications on
      // a stage where the feature has been rolled back.
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => Promise.all(regs.map((reg) => reg.unregister())))
        .catch((err) => console.warn('[sw] cleanup failed', err));
      return;
    }

    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((reg) => {
        if (cancelled) return;
        setRegistration(reg);
      })
      .catch((err) => {
        console.warn('[sw] registration failed', err);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { registration, isSupported };
}
